/**
 * Bangladeshi bank loan rate presets (indicative).
 * Update yearly. Source: bank websites & 2025 published rate cards.
 * Rates are typical advertised personal-loan rates unless noted.
 */

export interface BankRate {
  id: string;
  name: { en: string; bn: string };
  /** Typical advertised annual % */
  rates: {
    personal?: number;
    home?: number;
    car?: number;
    motorcycle?: number;
    business?: number;
    education?: number;
  };
}

export const BD_BANKS: BankRate[] = [
  { id: "custom", name: { en: "Custom", bn: "কাস্টম" }, rates: {} },
  { id: "dbbl", name: { en: "DBBL — Dutch-Bangla Bank", bn: "ডাচ-বাংলা ব্যাংক" }, rates: { personal: 12.5, home: 9.5, car: 10, business: 12 } },
  { id: "brac", name: { en: "BRAC Bank", bn: "ব্র্যাক ব্যাংক" }, rates: { personal: 13, home: 10, car: 10.5, business: 13, motorcycle: 12 } },
  { id: "city", name: { en: "City Bank", bn: "সিটি ব্যাংক" }, rates: { personal: 12, home: 9.75, car: 10, business: 12 } },
  { id: "ebl", name: { en: "EBL — Eastern Bank", bn: "ইস্টার্ন ব্যাংক" }, rates: { personal: 12.5, home: 10, car: 10.5, business: 12.5 } },
  { id: "prime", name: { en: "Prime Bank", bn: "প্রাইম ব্যাংক" }, rates: { personal: 13, home: 10.5, car: 11, business: 13 } },
  { id: "ific", name: { en: "IFIC Bank", bn: "আইএফআইসি ব্যাংক" }, rates: { personal: 12.5, home: 10, car: 11, business: 12.5 } },
  { id: "scb", name: { en: "Standard Chartered Bangladesh", bn: "স্ট্যান্ডার্ড চার্টার্ড" }, rates: { personal: 11.5, home: 9.5, car: 10 } },
  { id: "hsbc", name: { en: "HSBC Bangladesh", bn: "এইচএসবিসি বাংলাদেশ" }, rates: { personal: 12, home: 9.75 } },
  { id: "idlc", name: { en: "IDLC Finance", bn: "আইডিএলসি ফাইন্যান্স" }, rates: { personal: 13, home: 11, car: 11.5, motorcycle: 13, business: 13 } },
  { id: "lankabangla", name: { en: "Lanka Bangla Finance", bn: "লঙ্কা বাংলা ফাইন্যান্স" }, rates: { personal: 13.5, home: 11.25, car: 11.5, motorcycle: 13 } },
];

export type LoanType = "personal" | "home" | "car" | "motorcycle" | "business" | "education" | "custom";

export interface LoanTypePreset {
  id: LoanType;
  label: { en: string; bn: string };
  defaultRate: number;
  defaultYears: number;
  defaultAmount: number;
  /** Years range hint (display only) */
  yearsRange: [number, number];
}

export const LOAN_TYPES: LoanTypePreset[] = [
  { id: "personal", label: { en: "Personal Loan", bn: "পার্সোনাল লোন" }, defaultRate: 12.5, defaultYears: 5, defaultAmount: 500_000, yearsRange: [1, 5] },
  { id: "home", label: { en: "Home Loan", bn: "হোম লোন" }, defaultRate: 10, defaultYears: 20, defaultAmount: 5_000_000, yearsRange: [5, 25] },
  { id: "car", label: { en: "Car Loan", bn: "কার লোন" }, defaultRate: 10, defaultYears: 5, defaultAmount: 1_500_000, yearsRange: [1, 7] },
  { id: "motorcycle", label: { en: "Motorcycle Loan", bn: "মোটরসাইকেল লোন" }, defaultRate: 12, defaultYears: 3, defaultAmount: 200_000, yearsRange: [1, 4] },
  { id: "business", label: { en: "Business Loan", bn: "ব্যবসা লোন" }, defaultRate: 12.5, defaultYears: 5, defaultAmount: 1_000_000, yearsRange: [1, 7] },
  { id: "education", label: { en: "Education Loan", bn: "শিক্ষা লোন" }, defaultRate: 11, defaultYears: 7, defaultAmount: 800_000, yearsRange: [5, 10] },
  { id: "custom", label: { en: "Custom", bn: "কাস্টম" }, defaultRate: 12, defaultYears: 5, defaultAmount: 500_000, yearsRange: [1, 30] },
];

export const RATES_DISCLAIMER = {
  en: "Rates indicative — verify with the bank before applying.",
  bn: "প্রদর্শিত হার সাময়িক ধারণা — আবেদনের আগে ব্যাংকের সঙ্গে যাচাই করুন।",
};
